import type { Database } from '@pizzeria/types/database.types';

import { supabase, supabaseAdmin } from '../lib/supabaseClient';

type TaxSettingsRow = Database['public']['Tables']['tax_settings']['Row'];

export const TAX_SETTINGS_PLACEHOLDER_ID = '00000000-0000-0000-0000-000000000000';

export interface TaxSettingsRecord {
  id: string;
  defaultVatRate: number;
  defaultPricesIncludeVat: boolean;
  foodVatRate: number;
  foodPricesIncludeVat: boolean;
  drinkVatRate: number;
  drinkPricesIncludeVat: boolean;
  updatedAt: string | null;
}

const DEFAULT_TAX_SETTINGS: TaxSettingsRecord = {
  id: TAX_SETTINGS_PLACEHOLDER_ID,
  defaultVatRate: 10,
  defaultPricesIncludeVat: true,
  foodVatRate: 10,
  foodPricesIncludeVat: true,
  drinkVatRate: 10,
  drinkPricesIncludeVat: true,
  updatedAt: null,
};

const TAX_SELECT = `
  id,
  vat_rate,
  prices_include_vat,
  food_vat_rate,
  food_prices_include_vat,
  drink_vat_rate,
  drink_prices_include_vat,
  updated_at
`;

const mapTaxSettings = (row?: TaxSettingsRow | null): TaxSettingsRecord => {
  if (!row) {
    return DEFAULT_TAX_SETTINGS;
  }
  const normalizeRate = (value: number | null | undefined, fallback: number) =>
    Number.isFinite(value) ? Number(value) : fallback;
  const normalizeInclude = (value: boolean | null | undefined, fallback: boolean) =>
    typeof value === 'boolean' ? value : fallback;

  const defaultVatRate = normalizeRate(row.vat_rate, DEFAULT_TAX_SETTINGS.defaultVatRate);
  const defaultInclude = normalizeInclude(
    row.prices_include_vat,
    DEFAULT_TAX_SETTINGS.defaultPricesIncludeVat,
  );

  return {
    id: row.id,
    defaultVatRate,
    defaultPricesIncludeVat: defaultInclude,
    foodVatRate: normalizeRate(row.food_vat_rate, defaultVatRate),
    foodPricesIncludeVat: normalizeInclude(
      row.food_prices_include_vat,
      defaultInclude,
    ),
    drinkVatRate: normalizeRate(row.drink_vat_rate, defaultVatRate),
    drinkPricesIncludeVat: normalizeInclude(
      row.drink_prices_include_vat,
      defaultInclude,
    ),
    updatedAt: row.updated_at ?? null,
  };
};

export async function fetchTaxSettings(): Promise<TaxSettingsRecord> {
  const client = supabaseAdmin ?? supabase;

  const { data, error } = await client
    .from('tax_settings')
    .select(TAX_SELECT)
    .order('updated_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    throw error;
  }

  return mapTaxSettings(data as TaxSettingsRow | null);
}

export interface SaveTaxSettingsInput {
  id?: string;
  defaultVatRate: number;
  defaultPricesIncludeVat: boolean;
  foodVatRate: number;
  foodPricesIncludeVat: boolean;
  drinkVatRate: number;
  drinkPricesIncludeVat: boolean;
}

export async function saveTaxSettings(input: SaveTaxSettingsInput): Promise<TaxSettingsRecord> {
  const client = supabaseAdmin ?? supabase;

  const payload = {
    vat_rate: input.defaultVatRate,
    prices_include_vat: input.defaultPricesIncludeVat,
    food_vat_rate: input.foodVatRate,
    food_prices_include_vat: input.foodPricesIncludeVat,
    drink_vat_rate: input.drinkVatRate,
    drink_prices_include_vat: input.drinkPricesIncludeVat,
    updated_at: new Date().toISOString(),
  };

  const upsertPayload = input.id ? { ...payload, id: input.id } : payload;

  const { data, error } = await client
    .from('tax_settings')
    .upsert(upsertPayload, { onConflict: 'id' })
    .select(TAX_SELECT)
    .single();

  if (error) {
    throw error;
  }

  return mapTaxSettings(data as TaxSettingsRow);
}
